import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAmazonConnectAgent } from '@/hooks/useAmazonConnectAgent';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { api } from '@/store/api';
import type {
  ConnectAgent,
  ConnectContact,
  ConnectQueueEndpoint,
} from '@/types';

export type QueueTargetOption = {
  value: string;
  label: string;
  endpoint: ConnectQueueEndpoint;
};

type EndpointFields = {
  type?: unknown;
  name?: string;
  endpointARN?: string;
  endpointId?: string;
};

function isQueueEndpoint(endpoint: ConnectQueueEndpoint): boolean {
  return (endpoint as EndpointFields)?.type === 'queue';
}

/** Quick connect ID is the last segment of the endpoint ARN (.../transfer-destination/<id>). */
function getQuickConnectId(endpoint: ConnectQueueEndpoint): string | null {
  const { endpointARN, endpointId } = endpoint as EndpointFields;
  return endpointARN?.split('/').pop() || endpointId?.split('/').pop() || null;
}

/**
 * Queue whose quick connects are offered for transfer, matching the CCP: the queue the
 * contact came through, or the routing profile's default outbound queue when there is none.
 */
function getTransferQueueArn(
  contact: ConnectContact | undefined,
  agent: ConnectAgent | null
): string | null {
  try {
    const contactQueueArn = contact?.getQueue()?.queueARN;
    if (contactQueueArn) return contactQueueArn;
  } catch {
    // Contact may already be destroyed; fall back to the default outbound queue.
  }
  try {
    return agent?.getRoutingProfile?.()?.defaultOutboundQueue?.queueARN ?? null;
  } catch {
    return null;
  }
}

/**
 * Queue transfer targets for a contact, same as the CCP quick connect list (Streams
 * agent.getEndpoints), minus quick connects that point at disabled queues.
 * getEndpoints runs through the agent's CCP session, so it adds no backend Connect API calls.
 */
export function useQueueTransferOptions(contact: ConnectContact | undefined) {
  const { agent } = useAmazonConnectAgent();
  const { data: disabledQuickConnects } =
    api.useGetDisabledQueueQuickConnectsQuery();
  const [endpoints, setEndpoints] = useState<ConnectQueueEndpoint[]>([]);
  const [loadingQueues, setLoadingQueues] = useState(false);
  const requestIdRef = useRef(0);
  const queueArn = getTransferQueueArn(contact, agent);

  const refreshQueues = useCallback(() => {
    const requestId = ++requestIdRef.current;
    if (!agent || !queueArn) {
      setEndpoints([]);
      setLoadingQueues(false);
      return;
    }
    setLoadingQueues(true);
    connectAdapter.getQueueEndpoints(agent, [queueArn], {
      success: data => {
        if (requestId !== requestIdRef.current) return;
        setEndpoints(data.endpoints);
        setLoadingQueues(false);
      },
      failure: err => {
        if (requestId !== requestIdRef.current) return;
        console.warn('[useQueueTransferOptions] getEndpoints failed', err);
        setEndpoints([]);
        setLoadingQueues(false);
      },
    });
  }, [agent, queueArn]);

  useEffect(() => {
    refreshQueues();
  }, [refreshQueues]);

  const queueOptions = useMemo<QueueTargetOption[]>(() => {
    const hiddenIds = new Set(disabledQuickConnects?.quickConnectIds ?? []);
    return endpoints
      .filter(isQueueEndpoint)
      .filter(endpoint => {
        const id = getQuickConnectId(endpoint);
        return !id || !hiddenIds.has(id);
      })
      .map((endpoint, index) => {
        const { name, endpointARN } = endpoint as EndpointFields;
        return {
          value: endpointARN ?? String(index),
          label: name ?? `Queue ${index + 1}`,
          endpoint,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [endpoints, disabledQuickConnects]);

  return { queueOptions, loadingQueues, refreshQueues };
}
